//component to get all shot data for a given user and store it for use by multiple components on a page

import { useState, useEffect } from "react";
import ShotDataContext from "../context/shotDataContext";
import axios from "axios";

// Helper to get default 3-month date range
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return [start, end];
};

const ShotDataProvider = ({ children }) => {
  const [shotData, setShotData] = useState([]);
  const [sessionWasImported, setSessionWasImported] = useState(false);
  const [allShotEllipseData, setAllShotEllipseData] = useState([]);
  const [refreshData, setRefreshData] = useState(false);

  // Date range state - persists during session navigation
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [fetchedDateRange, setFetchedDateRange] = useState(null);

  //TODO - fetch all ellipse data

  const authFetch = axios.create({
    baseURL: "/api/v1",
  });

  const fetchShotData = async (startDate, endDate, append = false) => {
    const params = {};
    if (startDate && endDate) {
      params.startDate = startDate.toISOString();
      params.endDate = endDate.toISOString();
    }
    const { data } = await authFetch.get("/shots", { params });

    if (append) {
      // Merge with existing data, deduping by shot ID
      setShotData(prevData => {
        const existingIds = new Set(prevData.map(shot => shot._id));
        const newShots = data.filter(shot => !existingIds.has(shot._id));
        return [...prevData, ...newShots];
      });
    } else {
      setShotData(data);
    }
  };

  // Smart update: only fetch gaps in date range
  const updateDateRange = (newStart, newEnd) => {
    setDateRange([newStart, newEnd]);

    if (!fetchedDateRange) {
      // First fetch
      fetchShotData(newStart, newEnd);
      setFetchedDateRange([newStart, newEnd]);
      return;
    }

    // Calculate gaps to fetch
    const needsLeftFetch = newStart < fetchedDateRange[0];
    const needsRightFetch = newEnd > fetchedDateRange[1];

    if (needsLeftFetch) {
      // Fetch gap on left: newStart to day before fetchedDateRange[0]
      const gapEnd = new Date(fetchedDateRange[0]);
      gapEnd.setDate(gapEnd.getDate() - 1);
      fetchShotData(newStart, gapEnd, true); // append = true
    }

    if (needsRightFetch) {
      // Fetch gap on right: day after fetchedDateRange[1] to newEnd
      const gapStart = new Date(fetchedDateRange[1]);
      gapStart.setDate(gapStart.getDate() + 1);
      fetchShotData(gapStart, newEnd, true); // append = true
    }

    // Update tracked range if we fetched anything
    if (needsLeftFetch || needsRightFetch) {
      setFetchedDateRange([
        new Date(Math.min(newStart.getTime(), fetchedDateRange[0].getTime())),
        new Date(Math.max(newEnd.getTime(), fetchedDateRange[1].getTime()))
      ]);
    }
  };

  // Initial load with default 3-month range
  useEffect(() => {
    const [start, end] = getDefaultDateRange();
    fetchShotData(start, end);
    setFetchedDateRange([start, end]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when data is refreshed (import/delete)
  useEffect(() => {
    if (refreshData && fetchedDateRange) {
      // Clear and re-fetch current range
      setShotData([]);
      fetchShotData(fetchedDateRange[0], fetchedDateRange[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  //TODO - add a call to calculate them if we don't have them
  //  though only needed for first login post launch so may want to handle differently
  //  or not at all as on next import or session delete, they'll get
  useEffect(() => {
    const fetchAllShotEllipseData = async (currentUser) => {
      const { data } = await authFetch.get(
        "/ellipse/allShotsEllipses",
        currentUser
      );

      //TODO - if data is empty, trigger the calculation
      setAllShotEllipseData(data);
    };

    fetchAllShotEllipseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAverageCarryPerClub = (shots) => {
    const carryByClub = {};
    shots.forEach((shot) => {
      const { club, carry } = shot;
      if (!carryByClub[club]) {
        carryByClub[club] = { totalCarry: carry, count: 1 };
      } else {
        carryByClub[club].totalCarry += carry;
        carryByClub[club].count++;
      }
    });

    const averageCarryByClub = {};
    Object.keys(carryByClub).forEach((club) => {
      averageCarryByClub[club] =
        carryByClub[club].totalCarry / carryByClub[club].count;
    });
    return averageCarryByClub;
  };

  const removeShot = (shotId) => {
    const shotToRemove = shotData.find(shot => shot._id === shotId);
    if (shotToRemove) {
      const { Club } = shotToRemove;

      // Remove the shot from shotData
      setShotData(prevShotData => prevShotData.filter(shot => shot._id !== shotId));

      // Remove the ellipse data for the specific club
      setAllShotEllipseData(prevAllShotEllipseData =>
        prevAllShotEllipseData.filter(ellipse => ellipse.club !== Club)
      );
    }
  };

  const updateShot = (updatedShot) => {
    console.log("updating shot in context with: ", updatedShot);
    setShotData(prevShotData =>
      prevShotData.map(shot => shot._id === updatedShot._id ? updatedShot : shot)
    );
  };





  return (
    //TODO return ellipse data
    <ShotDataContext.Provider
      value={{
        shotData,
        allShotEllipseData,
        setAllShotEllipseData,
        removeShot,
        updateShot,
        getAverageCarryPerClub,
        setRefreshData,
        setSessionWasImported,
        sessionWasImported,
        dateRange,
        updateDateRange
      }}
    >
      {children}
    </ShotDataContext.Provider>
  );
};

export default ShotDataProvider;
